import React, { useEffect, useState, useCallback } from "react";
import client from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Boxes, Plus, ScanLine, Trash2, ClipboardCheck, LogOut, LogIn, QrCode } from "lucide-react";

const inp = "bg-[#0d0e11] border border-[#2C2D35] px-2.5 py-2 text-xs font-mono text-white outline-none focus:border-[#3B82F6] rounded-sm";
const STATUS_COLOR = { available: "#22C55E", checked_out: "#F59E0B", out_of_service: "#EF4444" };

function Stat({ label, value, color }) {
  return (
    <div className="bg-[#141518] border border-[#2C2D35] px-4 py-3 rounded-sm">
      <div className="font-mono text-[10px] text-[#9CA3AF] uppercase">{label}</div>
      <div className="font-mono font-bold text-2xl mt-1" style={color ? { color } : {}}>{value}</div>
    </div>
  );
}

export default function Fleet() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", category: "radio", serial: "", assigned_unit: "" });
  const [scan, setScan] = useState("");
  const [scanned, setScanned] = useState(null);
  const [qrAsset, setQrAsset] = useState(null);

  const load = useCallback(() => client.get("/fleet").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const addAsset = async () => {
    if (!form.name.trim()) { toast.error("Asset name required"); return; }
    try { await client.post("/fleet/assets", form); toast.success("Asset registered"); setForm({ name: "", category: "radio", serial: "", assigned_unit: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const doScan = async () => {
    if (!scan.trim()) return;
    try { const { data: a } = await client.get(`/fleet/scan/${encodeURIComponent(scan.trim())}`); setScanned(a); toast.success(`Found ${a.name}`); }
    catch (e) { setScanned(null); toast.error(e.response?.data?.detail || "No asset with that code"); }
  };
  const act = async (aid, action, body = {}) => {
    try { await client.post(`/fleet/assets/${aid}/${action}`, body); toast.success(`${action} recorded`); setScanned(null); setScan(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || `${action} failed`); }
  };
  const checkin = (a) => {
    const condition = window.prompt(`Check-in condition for ${a.name} (serviceable / damaged):`, "serviceable");
    if (condition === null) return;
    act(a.id, "checkin", { condition, notes: "" });
  };
  const inspect = (a) => {
    const result = window.prompt(`Inspection result for ${a.name} (pass / fail):`, "pass");
    if (!result) return;
    const notes = window.prompt("Inspection notes (optional):") || "";
    act(a.id, "inspect", { result: result.toLowerCase(), notes });
  };
  const remove = async (a) => {
    if (!window.confirm(`Retire asset ${a.name} (${a.code})?`)) return;
    try { await client.delete(`/fleet/assets/${a.id}`); toast.success("Asset retired"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const s = data?.summary;
  return (
    <Layout>
      <PageHeader title="Fleet & Equipment Accountability" sub="Asset registry · QR/scan custody chain · checkout / check-in · inspections" icon={Boxes} />
      <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="fleet-page">
        {s && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Assets" value={s.total} />
            <Stat label="Available" value={s.available} color="#22C55E" />
            <Stat label="Checked Out" value={s.checked_out} color={s.checked_out ? "#F59E0B" : undefined} />
            <Stat label="Out of Service" value={s.out_of_service} color={s.out_of_service ? "#EF4444" : undefined} />
            <Stat label="Never Inspected" value={s.inspection_due} color={s.inspection_due ? "#F59E0B" : undefined} />
          </div>
        )}

        {/* Scan station — hardware scanners type the code then Enter */}
        <div className="border border-[#2C2D35] rounded-sm" data-testid="fleet-scan-station">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold flex items-center gap-2">
            <ScanLine size={13} className="text-[#22D3EE]" /> SCAN STATION — scan or type an asset code (AST-XXXXXX)
          </div>
          <div className="p-3 flex flex-wrap gap-2 items-center">
            <input data-testid="fleet-scan-input" className={`${inp} w-56`} value={scan} placeholder="AST-…"
              onChange={(e) => setScan(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doScan()} autoFocus />
            <button data-testid="fleet-scan-btn" onClick={doScan} className="px-3 py-2 text-[10px] font-head font-bold bg-[#22D3EE] text-black rounded-sm">RESOLVE</button>
            {scanned && (
              <div className="flex items-center gap-3 flex-wrap border border-[#2C2D35] bg-[#141518] px-3 py-2 rounded-sm" data-testid="fleet-scan-result">
                <span className="font-mono text-xs text-white font-bold">{scanned.name}</span>
                <span className="font-mono text-[10px] uppercase" style={{ color: STATUS_COLOR[scanned.status] }}>{scanned.status.replace(/_/g, " ")}</span>
                {scanned.holder && <span className="font-mono text-[10px] text-[#F59E0B]">held by {scanned.holder}</span>}
                {scanned.status === "available" && <button data-testid="fleet-scan-checkout" onClick={() => act(scanned.id, "checkout")} className="px-2 py-1 text-[9px] font-head font-bold bg-[#F59E0B]/15 border border-[#F59E0B]/50 text-[#F59E0B] rounded-sm uppercase">Checkout to me</button>}
                {scanned.status === "checked_out" && <button data-testid="fleet-scan-checkin" onClick={() => checkin(scanned)} className="px-2 py-1 text-[9px] font-head font-bold bg-[#22C55E]/15 border border-[#22C55E]/50 text-[#4ade80] rounded-sm uppercase">Check in</button>}
              </div>
            )}
          </div>
        </div>

        {/* Register asset */}
        <div className="border border-[#2C2D35] rounded-sm">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">REGISTER ASSET (supervisors)</div>
          <div className="p-3 flex flex-wrap gap-2 items-end">
            <div><label className="block mb-0.5 font-mono text-[9px] text-[#6B7280] uppercase">Name</label><input data-testid="fleet-name" className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Portable Radio #12" /></div>
            <div><label className="block mb-0.5 font-mono text-[9px] text-[#6B7280] uppercase">Category</label>
              <select data-testid="fleet-category" className={inp} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {(data?.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select></div>
            <div><label className="block mb-0.5 font-mono text-[9px] text-[#6B7280] uppercase">Serial</label><input data-testid="fleet-serial" className={inp} value={form.serial} onChange={(e) => setForm({ ...form, serial: e.target.value })} placeholder="SN…" /></div>
            <div><label className="block mb-0.5 font-mono text-[9px] text-[#6B7280] uppercase">Assigned Unit</label><input data-testid="fleet-unit" className={inp} value={form.assigned_unit} onChange={(e) => setForm({ ...form, assigned_unit: e.target.value })} placeholder="e.g. 200" /></div>
            <button data-testid="fleet-add" onClick={addAsset} className="flex items-center gap-1 px-3 py-2 text-[10px] font-head font-bold bg-[#22D3EE] text-black rounded-sm"><Plus size={12} /> REGISTER</button>
          </div>
        </div>

        {/* Asset table */}
        <div className="border border-[#2C2D35] rounded-sm">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">ASSET REGISTRY</div>
          {(data?.assets || []).length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No assets registered. Supervisors register assets above; each gets a scannable QR code.</div>}
          {(data?.assets || []).map((a) => (
            <div key={a.id} className="flex items-center gap-2 px-3 py-2 border-b border-[#1E1F24] text-[11px] flex-wrap" data-testid={`fleet-asset-${a.code}`}>
              <button data-testid={`fleet-qr-${a.code}`} onClick={() => setQrAsset(qrAsset?.id === a.id ? null : a)} title="Show QR" className="text-[#22D3EE]"><QrCode size={14} /></button>
              <span className="font-mono text-[10px] text-[#22D3EE] w-24">{a.code}</span>
              <span className="text-white font-semibold w-44 truncate">{a.name}</span>
              <span className="font-mono text-[10px] text-[#6B7280] uppercase w-20">{a.category}</span>
              <span className="font-mono text-[10px] uppercase w-28" style={{ color: STATUS_COLOR[a.status] }}>{a.status.replace(/_/g, " ")}</span>
              <span className="font-mono text-[10px] text-[#F59E0B] w-32 truncate">{a.holder || ""}</span>
              <span className="font-mono text-[9px] text-[#6B7280] flex-1 truncate">
                {a.last_inspection ? `insp ${a.last_inspection.result.toUpperCase()} by ${a.last_inspection.by}` : "never inspected"}
              </span>
              {a.status === "available" && <button data-testid={`fleet-checkout-${a.code}`} onClick={() => act(a.id, "checkout")} className="flex items-center gap-1 px-2 py-1 text-[9px] font-head font-bold bg-[#F59E0B]/15 border border-[#F59E0B]/50 text-[#F59E0B] rounded-sm uppercase"><LogOut size={10} /> Checkout</button>}
              {a.status === "checked_out" && <button data-testid={`fleet-checkin-${a.code}`} onClick={() => checkin(a)} className="flex items-center gap-1 px-2 py-1 text-[9px] font-head font-bold bg-[#22C55E]/15 border border-[#22C55E]/50 text-[#4ade80] rounded-sm uppercase"><LogIn size={10} /> Check in</button>}
              <button data-testid={`fleet-inspect-${a.code}`} onClick={() => inspect(a)} className="flex items-center gap-1 px-2 py-1 text-[9px] font-head font-bold bg-[#3B82F6]/15 border border-[#3B82F6]/50 text-[#60A5FA] rounded-sm uppercase"><ClipboardCheck size={10} /> Inspect</button>
              <button data-testid={`fleet-del-${a.code}`} onClick={() => remove(a)} className="text-[#EF4444]"><Trash2 size={12} /></button>
              {qrAsset?.id === a.id && (
                <div className="w-full flex items-center gap-4 py-2" data-testid={`fleet-qr-panel-${a.code}`}>
                  <div className="bg-white p-2 rounded-sm"><QRCodeSVG value={a.code} size={96} /></div>
                  <div className="font-mono text-[10px] text-[#9CA3AF]">Print &amp; affix this QR to the asset.<br />Scanning it (or typing <span className="text-[#22D3EE]">{a.code}</span>) in the Scan Station resolves it instantly.</div>
                </div>
              )}
            </div>))}
        </div>

        {/* Accountability log */}
        <div className="border border-[#2C2D35] rounded-sm" data-testid="fleet-log">
          <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">ACCOUNTABILITY LOG</div>
          {(data?.log || []).length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No activity yet.</div>}
          {(data?.log || []).map((l, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-1.5 border-b border-[#1E1F24] font-mono text-[10px]">
              <span className="text-[#6B7280]">{(l.ts || "").slice(0, 16).replace("T", " ")}</span>
              <span className="text-[#22D3EE]">{l.code}</span>
              <span className="text-white">{l.name}</span>
              <span className="uppercase text-[#F59E0B]">{l.action.replace(/_/g, " ")}</span>
              <span className="text-[#9CA3AF] truncate">{l.detail}</span>
              <span className="ml-auto text-[#6B7280]">{l.by}</span>
            </div>))}
        </div>
      </div>
    </Layout>
  );
}
